import React from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    Box,
    Button,
} from "@mui/material";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import About from "./components/About";
import Education from "./components/Education";
import Experience from "./components/Experience";
import Publications from "./components/Publications";
import Contact from "./components/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";

const navItems = [
    { label: "About", path: "/about" },
    { label: "Experience", path: "/experience" },
    { label: "Education", path: "/education" },
    { label: "Publications", path: "/publications" },
    { label: "Blog", path: "/blog" },
    { label: "Contact", path: "/contact" },
];

const AnimatedRoutes: React.FC = () => {
    const location = useLocation();
    return (
        <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
                <Route path="/" element={<Navigate to="/about" />} />
                <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
                <Route path="/education" element={<PageWrapper><Education /></PageWrapper>} />
                <Route path="/experience" element={<PageWrapper><Experience /></PageWrapper>} />
                <Route path="/publications" element={<PageWrapper><Publications /></PageWrapper>} />
                <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
            </Routes>
        </AnimatePresence>
    );
};

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.35 }}
    >
        {children}
    </motion.div>
);

const NavLink: React.FC<{ label: string; path: string }> = ({ label, path }) => {
    const location = useLocation();
    const active = location.pathname === path || (path === "/blog" && location.pathname.startsWith("/blog"));

    return (
        <Button
            component={Link}
            to={path}
            sx={{
                color: active ? "primary.main" : "text.secondary",
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                px: 1.5,
                minWidth: 0,
                "&:hover": { color: "text.primary", background: "transparent" },
            }}
        >
            {label}
        </Button>
    );
};

const NavBar: React.FC = () => (
    <AppBar position="sticky" elevation={0}>
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, sm: 3 } }}>
            <Typography
                variant="body2"
                component={Link}
                to="/about"
                sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "13px",
                    color: "primary.main",
                    letterSpacing: "0.05em",
                    textDecoration: "none",
                }}
            >
                gautham.r
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
                {navItems.map(({ label, path }) => (
                    <NavLink key={path} label={label} path={path} />
                ))}
            </Box>
        </Toolbar>
    </AppBar>
);

const App: React.FC = () => (
    <Router>
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100vh"
            sx={{ bgcolor: "background.default" }}
        >
            <NavBar />

            <Box component="main" flexGrow={1}>
                <Container maxWidth="md" sx={{ py: 6 }}>
                    <AnimatedRoutes />
                </Container>
            </Box>

            <Box
                component="footer"
                sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    px: 4,
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "text.secondary", letterSpacing: "0.05em" }}
                >
                    © {new Date().getFullYear()} Gautham Rajendiran
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "text.secondary", letterSpacing: "0.05em" }}
                >
                    Vancouver, BC
                </Typography>
            </Box>
        </Box>
    </Router>
);

export default App;
